import React, { memo, useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';

import { Box, Button, CircularProgress, Link, Typography } from '@mui/material';
import DrawerCDS from '@sharedCR/coreDesignSystem/Drawer/DrawerCDS';
import { AutocompleteCDS, TextFieldCDS } from '@sharedCR/coreDesignSystem/form';
import { loanConditionApiV2 } from 'app/services/api';
import PropTypes from 'prop-types';

import useForm from 'hooks/useForm';
import { findPriorToOption, getPriorToPickerOptions, getPriorToRequestFields } from 'utils/priorTo';

import { showMessageAction } from 'store/global/actions';
import {
  getLoanBrokerConditionByApplicationIdAction,
  getLoanConditionByLoanIdAction,
  getLoanApplicationTasksAction,
  updateBrokerConditionAction,
} from 'store/loanApplications/actions';
import {
  selectConditionCategories,
  selectConditionCategoryOptions,
  selectPriorTo,
  selectPriorToAllOptions,
} from 'store/underwriting/selectors';

import { ConditionsTestIds } from '../Conditions.testIds';
import {
  getCategoryOptionLabel,
  getPriorToOptionLabel,
  isCategoryOptionEqual,
  isPriorToOptionEqual,
} from '../Conditions.utils';

import {
  COPY_FROM_CONDITION_LABEL,
  EDIT_CONDITION_FORM_ID,
  PARTNER_MESSAGE_HELPER_TEXT,
  TITLE_HELPER_TEXT,
} from './EditConditionModal.constants';
import {
  formStyles,
  helperTextStyles,
  partnerMessageHeaderStyles,
  partnerMessageWrapperStyles,
  saveButtonContentStyles,
} from './EditConditionModal.styles';
import { RequestTypes, resolveInitialCategory } from './EditConditionModal.utils';

const EditConditionModal = ({
  open,
  condition,
  isBrokerCompany,
  isEditAllowed,
  isManageAllowed,
  loanId,
  onClose,
}) => {
  const dispatch = useDispatch();
  const { applicationId } = useParams();
  const priorToOptions = useSelector(selectPriorTo);
  const allPriorToOptions = useSelector(selectPriorToAllOptions);
  const categoryOptions = useSelector(selectConditionCategoryOptions);
  const categories = useSelector(selectConditionCategories);

  const canonicalTitle = condition.canonicalTitle ?? '';

  const currentPriorToOption = useMemo(
    () =>
      findPriorToOption({
        options: allPriorToOptions,
        priorToTypeId: condition.priorToTypeId,
        priorToType: condition.priorToType,
      }),
    [allPriorToOptions, condition.priorToTypeId, condition.priorToType],
  );

  const priorToPickerOptions = useMemo(
    () => getPriorToPickerOptions({ options: priorToOptions, currentOption: currentPriorToOption }),
    [priorToOptions, currentPriorToOption],
  );

  const initialValues = useMemo(
    () => ({
      priorToType: currentPriorToOption,
      category: resolveInitialCategory({
        categoryOptions,
        categories,
        conditionCategoryId: condition.conditionCategoryId,
      }),
      internalText: condition.message ?? condition.internalText ?? '',
      borrowerInternalText: condition.borrowerMessage ?? condition.borrowerInternalText ?? '',
      subCompanyInternalText: condition.subCompanyMessage ?? condition.subCompanyInternalText ?? '',
    }),
    [categories, categoryOptions, condition, currentPriorToOption],
  );

  const { form, setInForm, isFormChanged } = useForm(initialValues);

  const [isSaving, setIsSaving] = useState(false);

  const isSignedOff = !!condition.signedOff;
  const isDeleted = !!condition.isDeleted;
  const isPriorToDisabled = !isManageAllowed || isSignedOff || isDeleted;
  const canEditMessages = isEditAllowed && !isSignedOff && !isDeleted;
  const drawerTitle = canonicalTitle
    ? `Edit Condition · ${canonicalTitle}`
    : `Edit Condition #${condition.number}`;

  const onPriorToChange = useCallback(
    (_event, value) => {
      setInForm('priorToType', value);
    },
    [setInForm],
  );

  const onCategoryChange = useCallback(
    (_event, value) => {
      setInForm('category', value);
    },
    [setInForm],
  );

  const onInternalTextChange = useCallback(
    e => {
      setInForm('internalText', e.target.value);
    },
    [setInForm],
  );

  const onBorrowerInternalTextChange = useCallback(
    e => {
      setInForm('borrowerInternalText', e.target.value);
    },
    [setInForm],
  );

  const onSubCompanyInternalTextChange = useCallback(
    e => {
      setInForm('subCompanyInternalText', e.target.value);
    },
    [setInForm],
  );

  const onCopyFromConditionMessage = useCallback(() => {
    setInForm('subCompanyInternalText', form.internalText);
  }, [form.internalText, setInForm]);

  const refreshConditions = useCallback(() => {
    const fetchConditions = isBrokerCompany
      ? getLoanBrokerConditionByApplicationIdAction({ applicationId })
      : getLoanConditionByLoanIdAction({ loanId, partialLoad: true, withDeleted: true });

    return Promise.all([dispatch(fetchConditions), dispatch(getLoanApplicationTasksAction())]);
  }, [applicationId, dispatch, isBrokerCompany, loanId]);

  const getLenderSubmission = useCallback(
    priorToFields => {
      if (canEditMessages) {
        return loanConditionApiV2.editLoanCondition({
          loanConditionId: condition.id,
          type: `${RequestTypes.UPDATE}.Lender`,
          internalText: form.internalText,
          borrowerInternalText: form.borrowerInternalText,
          subCompanyInternalText: form.subCompanyInternalText,
          conditionCategoryId: form.category?.value ?? null,
          ...priorToFields,
        });
      }

      return loanConditionApiV2.editConditionPriorToType({
        loanConditionsPriorToTypes: [
          {
            loanConditionId: condition.id,
            ...priorToFields,
          },
        ],
      });
    },
    [
      canEditMessages,
      condition.id,
      form.borrowerInternalText,
      form.category?.value,
      form.internalText,
      form.subCompanyInternalText,
    ],
  );

  const onSubmit = useCallback(
    event => {
      event.preventDefault();

      const priorToFields = getPriorToRequestFields(form.priorToType);

      const submission = isBrokerCompany
        ? dispatch(
            updateBrokerConditionAction({
              loanConditionId: condition.id,
              applicationId,
              internalText: form.internalText,
              borrowerInternalText: form.borrowerInternalText,
              subCompanyMessage: form.subCompanyInternalText,
              ...priorToFields,
            }),
          )
        : getLenderSubmission(priorToFields);

      setIsSaving(true);
      Promise.resolve(submission)
        .then(refreshConditions)
        .then(() =>
          dispatch(
            showMessageAction({
              message: `Condition #${condition.number}${
                canonicalTitle ? ` (${canonicalTitle})` : ''
              } updated.`,
              variant: 'success',
            }),
          ),
        )
        .then(onClose)
        .catch(() =>
          dispatch(showMessageAction({ message: 'Error while saving form', variant: 'error' })),
        )
        .finally(() => setIsSaving(false));
    },
    [
      applicationId,
      canonicalTitle,
      condition.id,
      condition.number,
      dispatch,
      form,
      getLenderSubmission,
      isBrokerCompany,
      onClose,
      refreshConditions,
    ],
  );

  const content = (
    <Box
      noValidate
      component="form"
      id={EDIT_CONDITION_FORM_ID}
      sx={formStyles}
      onSubmit={onSubmit}
    >
      {canonicalTitle && (
        <Box>
          <TextFieldCDS
            disabled
            fullWidth
            dataTestId={ConditionsTestIds.EDIT_TITLE_INPUT}
            label="Condition Title"
            name="canonicalTitle"
            value={canonicalTitle}
          />
          <Typography color="text.secondary" sx={helperTextStyles} variant="regular12">
            {TITLE_HELPER_TEXT}
          </Typography>
        </Box>
      )}

      <TextFieldCDS
        fullWidth
        multiline
        dataTestId={ConditionsTestIds.EDIT_CONDITION_MESSAGE_INPUT}
        disabled={!canEditMessages}
        label="Condition Message"
        name="internalText"
        rows={4}
        value={form.internalText}
        onChange={onInternalTextChange}
      />

      <TextFieldCDS
        fullWidth
        multiline
        dataTestId={ConditionsTestIds.EDIT_BORROWER_MESSAGE_INPUT}
        disabled={!canEditMessages}
        label="Borrower Message"
        name="borrowerInternalText"
        rows={3}
        value={form.borrowerInternalText}
        onChange={onBorrowerInternalTextChange}
      />

      <Box sx={partnerMessageWrapperStyles}>
        <Box sx={partnerMessageHeaderStyles}>
          <Typography color="text.primary" variant="medium14">
            Message to Partner
          </Typography>
          <Link
            color="primary.main"
            component="button"
            data-testid={ConditionsTestIds.EDIT_COPY_FROM_LINK}
            disabled={!canEditMessages}
            type="button"
            underline="hover"
            variant="regular12"
            onClick={onCopyFromConditionMessage}
          >
            {COPY_FROM_CONDITION_LABEL}
          </Link>
        </Box>
        <TextFieldCDS
          fullWidth
          multiline
          dataTestId={ConditionsTestIds.EDIT_SUB_COMPANY_MESSAGE_INPUT}
          disabled={!canEditMessages}
          name="subCompanyInternalText"
          rows={3}
          value={form.subCompanyInternalText}
          onChange={onSubCompanyInternalTextChange}
        />
        <Typography color="text.secondary" variant="regular12">
          {PARTNER_MESSAGE_HELPER_TEXT}
        </Typography>
      </Box>

      <AutocompleteCDS
        dataTestId={ConditionsTestIds.EDIT_CATEGORY_SELECT}
        disabled={!isManageAllowed || isSignedOff || isDeleted}
        getOptionLabel={getCategoryOptionLabel}
        isHidden={isBrokerCompany}
        isOptionEqualToValue={isCategoryOptionEqual}
        label="Category"
        name="category"
        options={categoryOptions}
        value={form.category}
        onChange={onCategoryChange}
      />

      <AutocompleteCDS
        disableClearable
        dataTestId={ConditionsTestIds.EDIT_PRIOR_TO_SELECT}
        disabled={isPriorToDisabled}
        getOptionLabel={getPriorToOptionLabel}
        isOptionEqualToValue={isPriorToOptionEqual}
        label="Prior To"
        name="priorToType"
        options={priorToPickerOptions}
        value={form.priorToType}
        withSortedOptions={false}
        onChange={onPriorToChange}
      />
    </Box>
  );

  const actions = (
    <Button
      data-testid={ConditionsTestIds.EDIT_SAVE_BUTTON}
      disabled={(isPriorToDisabled && !canEditMessages) || isSaving}
      form={EDIT_CONDITION_FORM_ID}
      type="submit"
      variant="contained"
    >
      <Box sx={saveButtonContentStyles}>
        {isSaving && <CircularProgress color="inherit" size={16} />}
        Save
      </Box>
    </Button>
  );

  return (
    <DrawerCDS
      actions={actions}
      content={content}
      data-testid={ConditionsTestIds.EDIT_DRAWER}
      isDirty={isFormChanged}
      isLoading={isSaving}
      open={open}
      title={drawerTitle}
      onClose={onClose}
    />
  );
};

EditConditionModal.propTypes = {
  open: PropTypes.bool.isRequired,
  condition: PropTypes.object.isRequired,
  isBrokerCompany: PropTypes.bool.isRequired,
  isEditAllowed: PropTypes.bool.isRequired,
  isManageAllowed: PropTypes.bool.isRequired,
  loanId: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default memo(EditConditionModal);
